import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppErrorBoundary from '@nxsys/components/AppErrorBoundary';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppTabGroupContainer from '@nxsys/components/AppTabGroupContainer';
import { PROSPECT_TYPE } from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Badge, Dropdown } from 'antd';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import AgencyTab from './components/Agency/AgencyTab';
import CreateAgencyModal from './components/Agency/CreateAgencyModal';
import CandidateTab from './components/Candidate/CandidateTab';
import CreateCandidateModal from './components/Candidate/CreateCandidateModal';
import CreateProspectModal from './components/Prospect/CreateProspectModal';
import ProspectTab from './components/Prospect/ProspectTab';
import RegistrationCompletedTab from './components/Prospect/RegistrationCompletedTab';

const Prospect = () => {
  const { messages } = useTypedIntl();

  const { isBureau, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { countProspectPending } = useSelector(
    (state: AppState) => state.prospectStore,
  );

  const onboardingLabel = (
    <span>
      {messages['prospect.tabs.onboarding']}
      {countProspectPending > 0 && (
        <Badge
          count={countProspectPending}
          size="small"
          style={{ marginLeft: 8 }}
        />
      )}
    </span>
  );

  const tabs = isAgency
    ? [
        {
          label: onboardingLabel,
          key: PROSPECT_TYPE.ONBOARDING,
        },
        {
          label: messages['prospect.tabs.registrationCompleted'],
          key: PROSPECT_TYPE.REGISTRATION_COMPLETED,
        },
      ]
    : [
        {
          label: messages['prospect.tabs.candidate'],
          key: PROSPECT_TYPE.CANDIDATE,
        },
        ...(!isBureau
          ? [
              {
                label: messages['prospect.tabs.agency'],
                key: PROSPECT_TYPE.AGENCY,
              },
            ]
          : []),
        {
          label: onboardingLabel,
          key: PROSPECT_TYPE.ONBOARDING,
        },
      ];

  const [activeTab, setActiveTab] = useState<PROSPECT_TYPE>(tabs[0]?.key);

  const [createCandidateModal, setCreateCandidateModal] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const toggleCreateCandidateModal = useCallback(() => {
    setCreateCandidateModal((prev) => ({ ...prev, open: !prev.open }));
  }, []);

  const [createAgencyModal, setCreateAgencyModal] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const toggleCreateAgencyModal = useCallback(() => {
    setCreateAgencyModal((prev) => ({ ...prev, open: !prev.open }));
  }, []);

  const [createProspectModal, setCreateProspectModal] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const toggleCreateProspectModal = useCallback(() => {
    setCreateProspectModal((prev) => ({ ...prev, open: !prev.open }));
  }, []);

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle
        prefix={messages['prospect.title']}
        separator=""
        title={''}
      />
    );
  }, [messages]);

  const renderAddNewButton = useCallback(
    (variant = 'solid') => {
      if (isAgency) {
        return (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            variant={variant as any}
            onClick={toggleCreateProspectModal}
          />
        );
      }

      // Every bureau — UK and Ireland alike — onboards through a candidate
      // first; only umbrella can also raise an agency
      const menuProps = {
        items: [
          {
            key: 'addNewCandidate',
            label: messages['prospect.addNewCandidate'],
            onClick: toggleCreateCandidateModal,
          },
          ...(!isBureau
            ? [
                {
                  key: 'addNewAgency',
                  label: messages['prospect.addNewAgency'],
                  onClick: toggleCreateAgencyModal,
                },
              ]
            : []),
        ],
      };

      return (
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            rightIcon={<DownOutlined />}
            variant={variant as any}
          />
        </Dropdown>
      );
    },
    [messages, isBureau, isAgency],
  );

  const renderRightContent = useCallback(() => {
    return isAgency ? null : renderAddNewButton();
  }, [renderAddNewButton, isAgency]);

  const handleTabChange = (key: PROSPECT_TYPE) => {
    setActiveTab(key);
  };

  const renderTabContent = (key: PROSPECT_TYPE) => {
    return {
      [PROSPECT_TYPE.CANDIDATE]: (
        <CandidateTab
          renderAddNewButton={renderAddNewButton}
          onProgressedToOnboarding={() =>
            setActiveTab(PROSPECT_TYPE.ONBOARDING)
          }
        />
      ),
      [PROSPECT_TYPE.AGENCY]: (
        <AgencyTab renderAddNewButton={renderAddNewButton} />
      ),
      [PROSPECT_TYPE.ONBOARDING]: (
        <ProspectTab
          onAddNew={toggleCreateProspectModal}
          renderAddNewButton={renderAddNewButton}
        />
      ),
      [PROSPECT_TYPE.REGISTRATION_COMPLETED]: <RegistrationCompletedTab />,
    }[key];
  };

  // FE-BUG-019: Wrap with ErrorBoundary
  return (
    <AppErrorBoundary>
      <div className="ProspectPage">
        <AppsContainer
          fullView
          type="bottom"
          title={renderTitle()}
          rightContent={renderRightContent()}
        >
          <AppTabGroupContainer
            items={tabs}
            defaultActiveTab={activeTab}
            handleTabGroupChange={handleTabChange}
          />
          {renderTabContent(activeTab)}
        </AppsContainer>
        <CreateCandidateModal
          isOpen={createCandidateModal.open}
          toggle={toggleCreateCandidateModal}
        />
        <CreateAgencyModal
          isOpen={createAgencyModal.open}
          toggle={toggleCreateAgencyModal}
        />
        <CreateProspectModal
          isOpen={createProspectModal.open}
          toggle={toggleCreateProspectModal}
        />
      </div>
    </AppErrorBoundary>
  );
};

export default Prospect;
